import React, { useState } from 'react';
import { Link } from 'wouter';
import { posts } from './BlogPage';
import PilotProgrammeSection from './components/PilotProgrammeSection';
import Footer from './components/Footer';

// Using static assets served from /public/assets to avoid bundler import issues
const logoHeader = '/assets/ALWAYS_logo_Web_RGB_POS.png';
const logoFooter = '/assets/ALWAYS_logo_Web_RGB_NEG.png';
const logoSymbol = '/assets/Grnheartlogo.png';

export default function AllwayswithyouLanding({
  heroImage = '/screens/hero-cpm-blurred.png',
  valueImage = '/screens/cpm-value-placeholder.png',
  careplanImage = '/screens/care-plan-sample.png',
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWatchVideo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const videoIframe = document.getElementById('overview-video') as HTMLIFrameElement;
    if (videoIframe) {
      // Request fullscreen
      if (videoIframe.requestFullscreen) {
        videoIframe.requestFullscreen();
      } else if ((videoIframe as any).webkitRequestFullscreen) {
        (videoIframe as any).webkitRequestFullscreen();
      } else if ((videoIframe as any).mozRequestFullScreen) {
        (videoIframe as any).mozRequestFullScreen();
      } else if ((videoIframe as any).msRequestFullscreen) {
        (videoIframe as any).msRequestFullscreen();
      }
      
      // Update the iframe src to include muted=0 parameter to enable audio
      const currentSrc = videoIframe.src;
      if (!currentSrc.includes('muted=')) {
        videoIframe.src = currentSrc + '&muted=0';
      }
    }
  };

  return (
    <div className="bg-slate-50 text-slate-900">
      {/* NAV */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logoHeader} alt="Allwayswithyou logo" className="h-8 w-auto" />
          </div>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#value" className="hover:underline">Why</a>
            <a href="#centre" className="hover:underline">Care Plan</a>
            <a href="#pilot-programme" className="hover:underline">Pilot Programme</a>
            <Link href="/blog" className="hover:underline" data-testid="link-blog">Blog/News</Link>
          </nav>
          <div className="flex items-center gap-3">
            <a href="https://cpm.allwayswithyou.com" target="_blank" rel="noopener noreferrer" data-testid="button-cpm-login" className="hidden sm:inline-flex px-4 py-2 rounded-lg text-white text-sm font-semibold transition-opacity hover:opacity-90" style={{ background: '#13384B' }}>CPM Login</a>
            {/* Hamburger — mobile only */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/></svg>
              )}
            </button>
          </div>
        </div>
        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-slate-100 bg-white px-4 py-3 flex flex-col gap-4 text-sm font-medium">
            <a href="#value"           onClick={() => setMobileMenuOpen(false)} className="hover:text-awy-dark-green">Why</a>
            <a href="#centre"          onClick={() => setMobileMenuOpen(false)} className="hover:text-awy-dark-green">Care Plan</a>
            <a href="#pilot-programme" onClick={() => setMobileMenuOpen(false)} className="hover:text-awy-dark-green">Pilot Programme</a>
            <Link href="/blog"         onClick={() => setMobileMenuOpen(false)} className="hover:text-awy-dark-green" data-testid="link-blog-mobile">Blog/News</Link>
            <a href="https://cpm.allwayswithyou.com" target="_blank" rel="noopener noreferrer"
               className="inline-flex w-max px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: '#13384B' }}>
              CPM Login
            </a>
          </nav>
        )}
      </header>

      {/* 1) HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #13384B 0%, #005D63 100%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28 grid md:grid-cols-2 gap-8 items-center">
          <div className="text-white">
            <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: '#51BF9D' }}>For Health and Social Care Providers</p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
              From assessment to care plan{' '}
              <span style={{ color: '#51BF9D' }}>in minutes.</span>
            </h1>
            <p className="mt-5 text-lg/7 max-w-xl" style={{ color: 'rgba(255,255,255,0.85)' }}>
              Turn structured assessments into personalised care plans. One platform for care planning, delivery, and insight.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="mailto:info@allwayswithyou.com" data-testid="button-schedule-demo" className="px-5 py-3 rounded-xl font-semibold transition-opacity hover:opacity-90" style={{ background: '#51BF9D', color: '#13384B' }}>Book a Demo →</a>
              <a href="#demo" onClick={handleWatchVideo} data-testid="button-watch-overview" className="px-5 py-3 rounded-xl font-semibold border cursor-pointer transition-colors hover:bg-white/10" style={{ background: 'transparent', color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>Watch Overview →</a>
            </div>
            <div className="mt-5 flex flex-wrap gap-5">
              <div className="flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="#51BF9D">
                  <path fillRule="evenodd" d="M10 1.944l7 3.111V10c0 4.163-2.98 7.826-7 8.944C5.98 17.826 3 14.163 3 10V5.055l7-3.111zm3.707 5.293a1 1 0 00-1.414 0L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 000-1.414z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">HIQA and HSE aligned</span>
              </div>
              <div className="flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="#51BF9D">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">Built for home, community and specialist services</span>
              </div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur rounded-2xl p-2 shadow-2xl">
            <img src={heroImage} alt="Allwayswithyou Care Plan Manager dashboard" className="w-full h-auto rounded-xl" />
          </div>
        </div>
      </section>

      {/* 2) VALUE PROPOSITION */}
      <section id="value" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          {/* Left: benefit cards */}
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#51BF9D' }}>Why Allwayswithyou</p>
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#13384B' }}>The smarter way to manage care plans</h2>
            <div className="space-y-5">
              {/* Card 1 */}
              <div className="flex gap-4 items-start">
                <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: '#E8F7F3' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1" style={{ color: '#13384B' }}>Minutes, not hours</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#666D89' }}>Generate compliant care plans directly from assessments — no re-keying, no delays.</p>
                </div>
              </div>
              {/* Card 2 */}
              <div className="flex gap-4 items-start">
                <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: '#E8F7F3' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1" style={{ color: '#13384B' }}>Everyone on the same page</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#666D89' }}>Role-based access for managers, assessors, carers and families — one live source of truth.</p>
                </div>
              </div>
              {/* Card 3 */}
              <div className="flex gap-4 items-start">
                <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: '#E8F7F3' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1" style={{ color: '#13384B' }}>Built for compliance</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#666D89' }}>HIQA and HSE aligned, with audit trails, version history, and digital approvals built in.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: video */}
          <div id="demo" className="rounded-2xl shadow-xl bg-white p-2">
            <div className="relative aspect-video rounded-xl overflow-hidden">
              <iframe 
                id="overview-video"
                src="https://share.synthesia.io/embeds/videos/36dbf691-1a57-48fc-9eaa-67d695ce1765?autoplay=1&loop=1"
                loading="lazy"
                title="Allwayswithyou Product Overview"
                allow="autoplay; encrypted-media; fullscreen;"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3) CARE PLAN AT THE CENTRE */}
      <section id="centre" className="py-16 md:py-20" style={{ background: '#F0FAF8' }}>
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#51BF9D' }}>Care Plan</p>
            <h2 className="text-3xl font-bold mb-3" style={{ color: '#13384B' }}>At the heart of every interaction</h2>
            <p className="max-w-xl mx-auto text-sm leading-relaxed" style={{ color: '#666D89' }}>
              Every assessment, team update, and family conversation connects back to a single structured care plan — live, compliant, and always up to date.
            </p>
          </div>

          {/* Hub & spoke diagram + sample image */}
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Mobile hub: centre card + 2-col grid of nodes (hidden on lg+) */}
            <div className="lg:hidden flex flex-col items-center gap-5">
              <div className="flex flex-col items-center justify-center w-28 h-28 rounded-full shadow-xl text-center"
                style={{ background: '#005D63' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mb-1" viewBox="0 0 24 24" fill="none" stroke="#51BF9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                <span className="text-white font-semibold leading-snug px-2 text-xs">Client's<br/>Personalised<br/>Care Plan</span>
              </div>
              <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
                {[
                  { label: 'Self Assessment',   icon: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></> },
                  { label: 'Home Assessment',   icon: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><circle cx="12" cy="13" r="2"/></> },
                  { label: 'Reassessment',      icon: <><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></> },
                  { label: 'Medical Records',   icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></> },
                  { label: "Client's Care Team", icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></> },
                  { label: 'HIQA Compliance',   icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></> },
                  { label: "Client's Family",   icon: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></> },
                  { label: 'Care Insights',     icon: <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></> },
                ].map(({ label, icon }) => (
                  <div key={label} className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm border" style={{ borderColor: '#D1EDE7' }}>
                    <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#E8F7F3' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
                    </div>
                    <span className="text-xs font-semibold leading-tight" style={{ color: '#13384B' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

          {/* Desktop hub diagram — 8 spokes, evenly spaced, container 460×420, centre (230,210), radius 135 */}
            <div className="hidden lg:block relative mx-auto" style={{ width: '460px', height: '420px' }}>
              {/* SVG spokes — from centre (230,210) to each node */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 460 420" xmlns="http://www.w3.org/2000/svg">
                {/* Top (230,75) */}
                <line x1="230" y1="210" x2="230" y2="75"  stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
                {/* Top-right (325,115) */}
                <line x1="230" y1="210" x2="325" y2="115" stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
                {/* Right (365,210) */}
                <line x1="230" y1="210" x2="365" y2="210" stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
                {/* Bottom-right (325,305) */}
                <line x1="230" y1="210" x2="325" y2="305" stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
                {/* Bottom (230,345) */}
                <line x1="230" y1="210" x2="230" y2="345" stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
                {/* Bottom-left (135,305) */}
                <line x1="230" y1="210" x2="135" y2="305" stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
                {/* Left (95,210) */}
                <line x1="230" y1="210" x2="95"  y2="210" stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
                {/* Top-left (135,115) */}
                <line x1="230" y1="210" x2="135" y2="115" stroke="#51BF9D" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7"/>
              </svg>

              {/* Centre circle — 116×116, centred at (230,210) → left=172, top=152 */}
              <div className="absolute z-10 flex flex-col items-center justify-center rounded-full shadow-xl text-center"
                style={{ background: '#005D63', left: '172px', top: '152px', width: '116px', height: '116px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mb-1" viewBox="0 0 24 24" fill="none" stroke="#51BF9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                <span className="text-white font-semibold leading-snug px-2" style={{ fontSize: '10px' }}>Client's<br/>Personalised<br/>Care Plan</span>
              </div>

              {/* Nodes — width 72px, left = nodeX−36, top = nodeY−32 */}

              {/* Top — Self Assessment (230,75) → left=194, top=43 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '194px', top: '43px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>Self<br/>Assessment</span>
              </div>

              {/* Top-right — Home Assessment (325,115) → left=289, top=83 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '289px', top: '83px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><circle cx="12" cy="13" r="2"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>Home<br/>Assessment</span>
              </div>

              {/* Right — Reassessment (365,210) → left=329, top=178 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '329px', top: '178px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>Reassessment</span>
              </div>

              {/* Bottom-right — Medical Records (325,305) → left=289, top=273 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '289px', top: '273px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>Medical<br/>Records</span>
              </div>

              {/* Bottom — Client's Care Team (230,345) → left=194, top=313 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '194px', top: '313px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>Client's<br/>Care Team</span>
              </div>

              {/* Bottom-left — HIQA Compliance (135,305) → left=99, top=273 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '99px', top: '273px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>HIQA<br/>Compliance</span>
              </div>

              {/* Left — Client's Family (95,210) → left=59, top=178 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '59px', top: '178px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>Client's<br/>Family</span>
              </div>

              {/* Top-left — Care Insights (135,115) → left=99, top=83 */}
              <div className="absolute z-10 flex flex-col items-center text-center" style={{ left: '99px', top: '83px', width: '72px' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-1 shadow-sm" style={{ background: 'white', border: '1.5px solid #51BF9D' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#005D63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
                  </svg>
                </div>
                <span className="font-semibold leading-tight" style={{ fontSize: '10px', color: '#13384B' }}>Care<br/>Insights</span>
              </div>
            </div>

            {/* Right: sample care plan image + caption */}
            <div>
              <div className="rounded-2xl shadow-lg overflow-hidden border" style={{ borderColor: '#D1EDE7' }}>
                <img src={careplanImage} alt="Sample Allwayswithyou care plan" className="w-full h-auto" />
              </div>
              <p className="text-center text-xs mt-3 font-medium" style={{ color: '#666D89' }}>
                A structured, digital care plan — generated in minutes from your assessments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4) PILOT PROGRAMME */}
      <PilotProgrammeSection />

      {/* 5) BLOG/NEWS PREVIEW */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">Latest News & Insights</h2>
            <p className="text-slate-700 max-w-2xl mx-auto">
              Stay informed with the latest updates, best practices, and announcements from Allwayswithyou.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.slice(0, 3).map((post) => (
              <div key={post.id} className="bg-white rounded-2xl shadow-lg p-6 flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                    {post.category}
                  </span>
                  <span className="text-slate-500 text-sm">{post.date}</span>
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">{post.title}</h3>
                <p className="text-slate-600 text-sm mb-4 flex-grow line-clamp-3">{post.excerpt}</p>
                <Link href={`/blog/${post.slug}`} data-testid={`link-blog-${post.slug}`} className="inline-flex items-center gap-1 text-slate-900 font-semibold hover:underline">
                  Read More →
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link href="/blog" data-testid="link-view-all-blog" className="inline-flex px-6 py-3 rounded-xl font-semibold transition-opacity hover:opacity-90" style={{ background: '#005D63', color: 'white' }}>
              View All Articles
            </Link>
          </div>
        </div>
      </section>

      {/* 6) FINAL CTA */}
      <section id="contact" className="py-20 md:py-28 text-white" style={{ background: 'linear-gradient(135deg, #13384B 0%, #005D63 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-sm font-semibold tracking-widest uppercase mb-4" style={{ color: '#51BF9D' }}>See it for yourself</p>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight">
            See what your team could do<br className="hidden md:block" /> with an hour back every day.
          </h2>
          <p className="mt-5 text-lg text-slate-300 max-w-xl mx-auto">
            Book a 30-minute walkthrough. We'll show you exactly how your assessments map to care plans — and answer every question your compliance lead will ask.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="mailto:info@allwayswithyou.com"
               className="px-7 py-4 rounded-xl text-base font-semibold transition-opacity hover:opacity-90 shadow-lg"
               style={{ background: '#51BF9D', color: '#13384B' }}>
              Book a Demo →
            </a>
            <a href="mailto:info@allwayswithyou.com"
               className="px-7 py-4 rounded-xl text-base font-semibold border border-white/30 hover:bg-white/10 transition-colors"
               style={{ color: 'white' }}>
              Send us a question
            </a>
          </div>
          {/* Trust micro-signals */}
          <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#51BF9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              HIQA &amp; HSE aligned
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#51BF9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              No lengthy onboarding
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#51BF9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              Built for leading home care teams
            </span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
